/**
 * Shared per-hospital unread notification counts.
 *
 * TopNav (dropdown badges) and NotificationPanel (bell) each call
 * useNotificationsAdvanced — without a shared store their React state diverges
 * and localStorage fights between instances. Firebase RTDB is the authoritative
 * source; optimistic mark-read decrements live here until the backend confirms.
 */

const STORAGE_KEY = "persisted-unread-counts";

export type HospitalUnreadCounts = Record<string, number>;

const listeners = new Set<(counts: HospitalUnreadCounts) => void>();

export const normalizeHospitalId = (
  id: string | number | null | undefined,
): string => String(id ?? "").trim();

const readPersisted = (): HospitalUnreadCounts => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return {};
    const parsed = JSON.parse(saved) as Record<string, unknown>;
    const normalized: HospitalUnreadCounts = {};
    Object.entries(parsed).forEach(([k, v]) => {
      const n = Number(v);
      if (normalizeHospitalId(k) && Number.isFinite(n) && n >= 0) {
        normalized[normalizeHospitalId(k)] = n;
      }
    });
    return normalized;
  } catch {
    return {};
  }
};

let counts: HospitalUnreadCounts = readPersisted();

const persist = () => {
  try {
    if (Object.keys(counts).length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(counts));
    }
  } catch {
    /* ignore quota errors */
  }
};

const notify = () => {
  const snapshot = { ...counts };
  listeners.forEach((fn) => fn(snapshot));
};

export const getHospitalUnreadCounts = (): HospitalUnreadCounts => ({
  ...counts,
});

export const getHospitalUnreadCount = (
  hospitalId: string | number | null | undefined,
): number => {
  const key = normalizeHospitalId(hospitalId);
  if (!key) return 0;
  return counts[key] ?? 0;
};

export const setHospitalUnreadCount = (
  hospitalId: string | number,
  count: number,
) => {
  const key = normalizeHospitalId(hospitalId);
  if (!key) return;
  const next = Math.max(0, Math.floor(count));
  if (counts[key] === next) return;
  counts = { ...counts, [key]: next };
  persist();
  notify();
};

export const patchHospitalUnreadCounts = (
  patch: HospitalUnreadCounts,
  options?: { replaceFetched?: string[] },
) => {
  let next = { ...counts };

  if (options?.replaceFetched?.length) {
    options.replaceFetched.forEach((id) => {
      const key = normalizeHospitalId(id);
      if (key) next[key] = 0;
    });
  }

  let changed = false;
  Object.entries(patch).forEach(([k, v]) => {
    const key = normalizeHospitalId(k);
    if (!key) return;
    const n = Math.max(0, Math.floor(Number(v) || 0));
    if (next[key] !== n) {
      next[key] = n;
      changed = true;
    }
  });

  if (!changed && !options?.replaceFetched?.length) return;
  counts = next;
  persist();
  notify();
};

export const decrementHospitalUnreadCount = (
  hospitalId: string | number | null | undefined,
  by = 1,
) => {
  const key = normalizeHospitalId(hospitalId);
  if (!key) return;
  setHospitalUnreadCount(key, Math.max(0, (counts[key] || 0) - by));
};

export const clearHospitalUnreadCount = (
  hospitalId: string | number | null | undefined,
) => {
  setHospitalUnreadCount(normalizeHospitalId(hospitalId), 0);
};

export const subscribeHospitalUnreadCounts = (
  listener: (counts: HospitalUnreadCounts) => void,
) => {
  listeners.add(listener);
  listener({ ...counts });
  return () => {
    listeners.delete(listener);
  };
};
